import React, { useEffect, useState } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  MenuItem,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

import StockTransferService from "../../services/StockTransferService";

const PAGE_SIZE = 50;


/**
 * ============================================================
 * FACTORY DISPATCH
 * ============================================================
 *
 * Factory user flow:
 *
 * factorymanager
 *      ↓
 * backend resolves party = factory
 *      ↓
 * available stock
 *      ↓
 * select stock
 *      ↓
 * select manufacturer
 *      ↓
 * dispatch
 *      ↓
 * pending inbound register
 *      ↓
 * manufacturer accepts/rejects
 *
 * IMPORTANT:
 * No user type is sent from frontend.
 */
const FactoryDispatch = () => {

  /**
   * ==========================================================
   * STATE
   * ==========================================================
   */

  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(false);

  const [rows, setRows] = useState([]);

  const [rowCount, setRowCount] = useState(0);

  const [selectedRows, setSelectedRows] = useState([]);

  const [manufacturers, setManufacturers] = useState([]);

  const [manufacturerId, setManufacturerId] = useState("");

  const [dispatchDate, setDispatchDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const [trackingNo, setTrackingNo] = useState("");

  const [dispatchMode, setDispatchMode] = useState("");

  const [comments, setComments] = useState("");

  const [dispatchResult, setDispatchResult] = useState([]);

  const [registerId, setRegisterId] = useState(null);

  const [resultOpen, setResultOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });


  /**
   * ==========================================================
   * SNACKBAR
   * ==========================================================
   */

  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message,
    });
  };


  const closeSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };


  /**
   * ==========================================================
   * LOAD AVAILABLE STOCK
   * ==========================================================
   *
   * POST
   * /api/stock/transfer/available/
   *
   * IMPORTANT:
   *
   * We don't send:
   *
   * user_type
   * role
   * party_type
   *
   * Backend automatically determines:
   *
   * factorymanager -> factory
   */
  const loadStock = async () => {

    setLoading(true);

    try {

      const response =
        await StockTransferService.getAvailableStock({
          page,
          page_size: PAGE_SIZE,
        });


      const data = response?.data || {};


      setRows(
        Array.isArray(data.results)
          ? data.results
          : []
      );


      setRowCount(
        Number(data.total || 0)
      );


      setSelectedRows([]);


    } catch (error) {

      console.error(
        "Available stock error:",
        error
      );

      showSnackbar(
        error.response?.data?.message ||
          error.response?.data?.detail ||
          "Unable to load available stock",
        "error"
      );

    } finally {

      setLoading(false);
    }
  };


  /**
   * ==========================================================
   * LOAD DESTINATIONS
   * ==========================================================
   *
   * POST
   * /api/stock/transfer/destinations/
   *
   * Since this page is factory dispatch:
   *
   * factory -> manufacturer
   *
   * We don't send to_type here because for factory
   * the backend automatically returns Manufacturer rows.
   */
  const loadManufacturers = async () => {

    try {

      const response =
        await StockTransferService.getTransferDestinations({});


      const destinations =
        response?.data?.destinations || [];


      setManufacturers(destinations);


      if (!destinations.length) {

        showSnackbar(
          "No manufacturers available",
          "info"
        );
      }

    } catch (error) {

      console.error(
        "Destination error:",
        error
      );

      showSnackbar(
        error.response?.data?.message ||
          error.response?.data?.detail ||
          "Unable to load manufacturers",
        "error"
      );
    }
  };


  /**
   * ==========================================================
   * INITIAL LOAD
   * ==========================================================
   */

  useEffect(() => {

    loadStock();

  }, [page]);


  useEffect(() => {

    loadManufacturers();

  }, []);


  /**
   * ==========================================================
   * DISPATCH
   * ==========================================================
   *
   * POST
   * /api/stock/transfer/send/
   *
   * Factory -> Manufacturer
   *
   * Payload:
   *
   * {
   *   ids: [...],
   *   to_type: "manufacturer",
   *   to_id: manufacturerId,
   *   dispatch_date: "2026-08-14",
   *   tracking_no: "...",
   *   dispatch_mode: "...",
   *   comments: "..."
   * }
   *
   * NO user_type.
   */
  const handleDispatch = async () => {

    /**
     * Validate selection
     */
    if (!selectedRows.length) {

      showSnackbar(
        "Please select at least one stock item",
        "warning"
      );

      return;
    }


    /**
     * Validate manufacturer
     */
    if (!manufacturerId) {

      showSnackbar(
        "Please select a manufacturer",
        "warning"
      );

      return;
    }


    /**
     * Validate dispatch date
     */
    if (!dispatchDate) {

      showSnackbar(
        "Dispatch date is required",
        "warning"
      );

      return;
    }


    try {

      setLoading(true);


      const payload = {

        /**
         * Selected StockMaster IDs
         */
        ids: selectedRows,

        /**
         * Factory -> Manufacturer
         */
        to_type: "manufacturer",

        /**
         * Manufacturer ID
         */
        to_id: manufacturerId,

        /**
         * Required date
         */
        dispatch_date: dispatchDate,

        /**
         * Optional fields
         */
        tracking_no: trackingNo.trim(),

        dispatch_mode: dispatchMode.trim(),

        comments: comments.trim(),
      };


      console.log(
        "Unified dispatch payload:",
        payload
      );


      const response =
        await StockTransferService.sendTransfer(
          payload
        );


      const data =
        response?.data || {};


      /**
       * Store results
       */
      setDispatchResult(
        Array.isArray(data.results)
          ? data.results
          : []
      );


      setRegisterId(
        data.register_id || null
      );


      setResultOpen(true);


      /**
       * Count successful items
       */
      const results =
        Array.isArray(data.results)
          ? data.results
          : [];


      const successCount =
        results.filter(
          (item) =>
            item.status === "success"
        ).length;


      const errorCount =
        results.filter(
          (item) =>
            item.status === "error"
        ).length;


      if (successCount > 0 && errorCount > 0) {

        showSnackbar(
          `${successCount} item(s) dispatched successfully, ${errorCount} failed`,
          "warning"
        );

      } else if (successCount > 0) {

        showSnackbar(
          `${successCount} item(s) dispatched successfully`,
          "success"
        );

      } else {

        showSnackbar(
          "Dispatch failed",
          "error"
        );
      }


      /**
       * Reload available stock
       */
      await loadStock();


      /**
       * Clear selected items
       */
      setSelectedRows([]);


    } catch (error) {

      console.error(
        "Dispatch error:",
        error
      );


      showSnackbar(
        error.response?.data?.message ||
          error.response?.data?.detail ||
          "Dispatch failed",
        "error"
      );

    } finally {

      setLoading(false);
    }
  };


  /**
   * ==========================================================
   * DATAGRID COLUMNS
   * ==========================================================
   */

  const columns = [

    {
      field: "id",
      headerName: "ID",
      width: 90,
    },

    {
      field: "uid",
      headerName: "UID",
      width: 180,
    },

    {
      field: "imei",
      headerName: "IMEI",
      width: 180,
    },

    {
      field: "device_esn",
      headerName: "ESN",
      width: 180,
    },

    {
      field: "device_model_code",
      headerName: "Model",
      width: 180,
    },

    {
      field: "config_done_at",
      headerName: "Configured At",
      width: 220,
    },
  ];


  /**
   * ==========================================================
   * RENDER
   * ==========================================================
   */

  return (
    <Box p={3}>

      {/* ======================================================
          PAGE HEADER
      ====================================================== */}

      <Card sx={{ mb: 3 }}>

        <CardContent>

          <Typography
            variant="h5"
            fontWeight={700}
            color="primary"
            gutterBottom
          >
            Factory Dispatch
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
          >
            Select available factory stock and dispatch it
            to a manufacturer.
          </Typography>

        </CardContent>

      </Card>


      {/* ======================================================
          AVAILABLE STOCK
      ====================================================== */}

      <Card>

        <CardContent>

          <Typography
            variant="h6"
            color="primary"
            fontWeight={700}
            mb={2}
          >
            Available Factory Stock
          </Typography>


          <Box
            sx={{
              height: 500,
              width: "100%",
            }}
          >

            <DataGrid

              rows={rows}

              columns={columns}

              loading={loading}

              checkboxSelection

              paginationMode="server"

              rowCount={rowCount}

              pageSizeOptions={[PAGE_SIZE]}

              paginationModel={{
                page: page - 1,
                pageSize: PAGE_SIZE,
              }}

              onPaginationModelChange={(model) => {

                setPage(
                  model.page + 1
                );

              }}

              getRowId={(row) =>
                row.id
              }

              rowSelectionModel={
                selectedRows
              }

              onRowSelectionModelChange={
                (ids) => {

                  setSelectedRows(
                    ids
                  );

                }
              }

              disableRowSelectionOnClick

            />

          </Box>

        </CardContent>

      </Card>


      {/* ======================================================
          DISPATCH FORM
      ====================================================== */}

      <Card sx={{ mt: 3 }}>

        <CardContent>

          <Typography
            variant="h6"
            color="primary"
            fontWeight={700}
            mb={2}
          >
            Dispatch Details
          </Typography>


          <Grid
            container
            spacing={2}
          >

            {/* Manufacturer */}

            <Grid
              item
              xs={12}
              md={4}
            >

              <TextField
                select
                fullWidth
                label="Manufacturer"
                value={manufacturerId}
                onChange={(e) =>
                  setManufacturerId(
                    e.target.value
                  )
                }
              >

                {manufacturers.map(
                  (manufacturer) => (

                    <MenuItem
                      key={
                        manufacturer.id
                      }
                      value={
                        manufacturer.id
                      }
                    >
                      {
                        manufacturer.name
                      }
                    </MenuItem>

                  )
                )}

              </TextField>

            </Grid>


            {/* Dispatch Date */}

            <Grid
              item
              xs={12}
              md={4}
            >

              <TextField
                fullWidth
                type="date"
                label="Dispatch Date"
                value={dispatchDate}
                onChange={(e) =>
                  setDispatchDate(
                    e.target.value
                  )
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />

            </Grid>


            {/* Tracking */}

            <Grid
              item
              xs={12}
              md={4}
            >

              <TextField
                fullWidth
                label="Tracking No."
                value={trackingNo}
                onChange={(e) =>
                  setTrackingNo(
                    e.target.value
                  )
                }
                placeholder="Optional"
              />

            </Grid>


            {/* Dispatch Mode */}

            <Grid
              item
              xs={12}
              md={4}
            >

              <TextField
                select
                fullWidth
                label="Dispatch Mode"
                value={dispatchMode}
                onChange={(e) =>
                  setDispatchMode(
                    e.target.value
                  )
                }
              >

                <MenuItem value="">
                  None
                </MenuItem>

                <MenuItem value="courier">
                  Courier
                </MenuItem>

                <MenuItem value="transport">
                  Transport
                </MenuItem>

                <MenuItem value="pickup">
                  Pickup
                </MenuItem>

                <MenuItem value="other">
                  Other
                </MenuItem>

              </TextField>

            </Grid>


            {/* Comments */}

            <Grid
              item
              xs={12}
              md={8}
            >

              <TextField
                fullWidth
                label="Comments"
                value={comments}
                onChange={(e) =>
                  setComments(
                    e.target.value
                  )
                }
                multiline
                minRows={1}
                placeholder="Optional comments"
              />

            </Grid>


            {/* Selected */}

            <Grid
              item
              xs={12}
              md={4}
              display="flex"
              alignItems="center"
              gap={2}
            >

              <Chip
                color="primary"
                label={`${selectedRows.length} Selected`}
              />

            </Grid>


            {/* Dispatch */}

            <Grid
              item
              xs={12}
              md={4}
            >

              <Button
                fullWidth
                variant="contained"
                color="success"
                size="large"
                onClick={
                  handleDispatch
                }
                disabled={
                  loading ||
                  selectedRows.length === 0 ||
                  !manufacturerId ||
                  !dispatchDate
                }
              >

                {loading ? (
                  <CircularProgress
                    size={24}
                    color="inherit"
                  />
                ) : (
                  "Dispatch Selected"
                )}

              </Button>

            </Grid>

          </Grid>

        </CardContent>

      </Card>


      {/* ======================================================
          DISPATCH RESULT
      ====================================================== */}

      <Dialog
        open={resultOpen}
        onClose={() =>
          setResultOpen(false)
        }
        maxWidth="md"
        fullWidth
      >

        <DialogTitle>

          Dispatch Result

          {registerId && (
            <Typography
              variant="body2"
              color="text.secondary"
              mt={0.5}
            >
              Register ID:{" "}
              <strong>
                {registerId}
              </strong>
            </Typography>
          )}

        </DialogTitle>


        <DialogContent>

          <DataGrid
            autoHeight
            hideFooter
            rows={dispatchResult}
            getRowId={(row, index) =>
              row.id ||
              row.item_id ||
              `${index}`
            }
            columns={[
              {
                field: "id",
                headerName: "ID",
                width: 100,
              },

              {
                field: "item_id",
                headerName: "Item ID",
                width: 100,
              },

              {
                field: "imei",
                headerName: "IMEI",
                flex: 1,
                minWidth: 180,
              },

              {
                field: "status",
                headerName: "Status",
                width: 160,

                renderCell: (
                  params
                ) => (

                  <Chip
                    label={
                      params.value
                    }
                    color={
                      params.value ===
                      "success"
                        ? "success"
                        : "error"
                    }
                  />

                ),
              },

              {
                field: "error",
                headerName: "Error",
                flex: 1,
                minWidth: 250,
              },
            ]}
          />

        </DialogContent>


        <DialogActions>

          <Button
            onClick={() =>
              setResultOpen(false)
            }
          >
            Close
          </Button>

        </DialogActions>

      </Dialog>


      {/* ======================================================
          SNACKBAR
      ====================================================== */}

      <Snackbar
        open={
          snackbar.open
        }
        autoHideDuration={4000}
        onClose={
          closeSnackbar
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >

        <Alert
          severity={
            snackbar.severity
          }
          variant="filled"
          onClose={
            closeSnackbar
          }
        >

          {
            snackbar.message
          }

        </Alert>

      </Snackbar>

    </Box>
  );
};


export default FactoryDispatch;